import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { api } from '@/lib/api'
import { subscribe } from '@/lib/centrifugo'

export const Route = createFileRoute('/tokens/')({
  component: TokensPage,
})

type ImportInfo = { total: number; imported: number; status: string; error: string | null }

function TokensPage() {
  const [tokens, setTokens] = useState<any[]>([])
  const [mint, setMint] = useState('')
  const [symbol, setSymbol] = useState('')
  const [importStatus, setImportStatus] = useState<Record<string, ImportInfo>>({})

  const load = () => api.getTokens().then(setTokens).catch(() => {})

  useEffect(() => {
    load()
    api.getImportStatus().then(setImportStatus).catch(() => {})

    let unsub: (() => void) | null = null
    subscribe('live:import', (data: ImportInfo & { mint: string }) => {
      setImportStatus((prev) => ({ ...prev, [data.mint]: data }))
    }).then((fn) => { unsub = fn })

    return () => { unsub?.() }
  }, [])

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!mint.trim()) return
    await api.addToken(mint.trim(), symbol.trim() || undefined)
    setMint('')
    setSymbol('')
    load()
  }

  const handleRemove = async (m: string) => {
    await api.removeToken(m)
    load()
  }

  const handleRefetch = async (m: string) => {
    try {
      await api.refetchMetadata(m)
      load()
    } catch {}
  }

  const hasAnyImport = Object.keys(importStatus).length > 0

  return (
    <div>
      <h1 style={{ fontSize: 20, marginBottom: 20 }}>Watched Tokens</h1>

      <form onSubmit={handleAdd} style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        <input placeholder="Token mint address" value={mint} onChange={(e) => setMint(e.target.value)} style={{ flex: 1 }} />
        <input placeholder="Symbol (optional)" value={symbol} onChange={(e) => setSymbol(e.target.value)} style={{ width: 140 }} />
        <button type="submit" className="btn btn-primary">Add</button>
      </form>

      <div style={{ maxHeight: 'calc(100vh - 200px)', overflow: 'auto' }}>
      <table>
        <thead>
          <tr>
            <th>Mint</th>
            <th>Symbol</th>
            <th>Added</th>
            {hasAnyImport && <th>Import</th>}
            <th></th>
          </tr>
        </thead>
        <tbody>
          {tokens.map((t: any) => {
            const imp = importStatus[t.mint]
            return (
              <tr key={t.mint}>
                <td style={{ fontFamily: 'monospace', fontSize: 12 }}>
                  <a href={`/tokens/${t.mint}`}>{t.mint}</a>
                </td>
                <td>{t.symbol || '-'}</td>
                <td style={{ color: 'var(--text-dim)', fontSize: 12 }}>{new Date(t.added_at).toLocaleString()}</td>
                {hasAnyImport && (
                  <td>
                    {imp ? <ImportBadge info={imp} /> : null}
                  </td>
                )}
                <td style={{ display: 'flex', gap: 4 }}>
                  <button className="btn btn-sm" onClick={() => handleRefetch(t.mint)}>Refetch</button>
                  <button className="btn btn-danger btn-sm" onClick={() => handleRemove(t.mint)}>Remove</button>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
      </div>
      {tokens.length === 0 && <div style={{ color: 'var(--text-dim)', padding: 20, textAlign: 'center' }}>No tokens watched</div>}
    </div>
  )
}

function ImportBadge({ info }: { info: ImportInfo }) {
  if (info.status === 'error') {
    return (
      <span style={{ color: '#ef4444', fontSize: 12 }} title={info.error || ''}>
        Error
      </span>
    )
  }

  if (info.status === 'discovering') {
    return (
      <span style={{ color: '#f59e0b', fontSize: 12 }}>
        Discovering...
      </span>
    )
  }

  if (info.status === 'done') {
    return (
      <span style={{ color: '#22c55e', fontSize: 12 }}>
        {info.total}/{info.total} done
      </span>
    )
  }

  const pct = info.total > 0 ? Math.round((info.imported / info.total) * 100) : 0

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <div style={{
        width: 60,
        height: 6,
        background: 'var(--border, #333)',
        borderRadius: 3,
        overflow: 'hidden',
      }}>
        <div style={{
          width: `${pct}%`,
          height: '100%',
          background: '#3b82f6',
          borderRadius: 3,
          transition: 'width 0.3s',
        }} />
      </div>
      <span style={{ color: 'var(--text-dim)', fontSize: 12, whiteSpace: 'nowrap' }}>
        {info.imported}/{info.total}
      </span>
    </div>
  )
}
